// ==========================
// Version 2 — MiniTimelineStrip.js (single default export)
// ==========================
import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';

export default function MiniTimelineStrip({
  memories = [],
  activeId,
  onActiveChange,
}) {
  const listRef = useRef(null);

  // Scroll the active card into view
  useEffect(() => {
    if (!activeId || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-id="${activeId}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
    }
  }, [activeId]);

  const items = [...memories].sort((a, b) =>
    String(b.date || '').localeCompare(String(a.date || ''))
  );

  return (
    <div style={styles.wrap}>
      <div style={styles.rail} ref={listRef}>
        {items.map((m) => {
          const id = m.firestoreId || m.id;
          const image =
            Array.isArray(m.imageUrls) && m.imageUrls.length ? m.imageUrls[0] : null;
          const linkTo = m.firestoreId ? `/m/${m.firestoreId}` : '#';
          const isActive = id === activeId;

          return (
            <div
              key={id}
              data-id={id}
              style={{ ...styles.card, ...(isActive ? styles.cardActive : null) }}
              onMouseEnter={() => onActiveChange?.(id)}
              onClick={() => onActiveChange?.(id)}
            >
              {image ? (
                <Link to={linkTo} state={{ memory: m }} style={{ textDecoration: 'none' }}>
                  <img src={image} alt="" style={styles.img} loading="lazy" />
                </Link>
              ) : (
                <div style={styles.placeholder}>No Image</div>
              )}
              <div style={styles.meta}>
                <div style={styles.date}>{m.date || '—'}</div>
                {m.story ? <div style={styles.excerpt}>{truncate(m.story, 60)}</div> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

const styles = {
  wrap: {
    position: 'relative',
    background: 'rgba(253,252,249,0.06)',
    border: '1px solid rgba(201,164,76,0.25)',
    borderRadius: 16,
    padding: 8,
    overflow: 'hidden',
  },
  rail: {
    display: 'flex',
    gap: 10,
    overflowX: 'auto',
    paddingBottom: 6,
    scrollbarWidth: 'thin',
  },
  card: {
    flex: '0 0 240px',
    background: IVORY,
    color: NAVY,
    border: '1px solid rgba(201,164,76,0.35)',
    borderRadius: 14,
    boxShadow: '0 8px 24px rgba(10,26,51,0.22)',
    cursor: 'pointer',
  },
  cardActive: {
    outline: `2px solid ${GOLD}`,
    outlineOffset: 2,
  },
  img: {
    width: '100%',
    height: 130,
    objectFit: 'cover',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    display: 'block',
  },
  placeholder: {
    width: '100%',
    height: 130,
    display: 'grid',
    placeItems: 'center',
    color: WARM_GRAY,
    background: '#fff',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  meta: { padding: 8 },
  date: { fontFamily: 'serif', fontWeight: 700 },
  excerpt: { color: WARM_GRAY, fontSize: 12, marginTop: 2 },
};
// ==========================
// End Version 2 — MiniTimelineStrip.js
// ==========================
